const STATUS_LABEL = {
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  TENTATIVE: 'Maybe',
  INVITED: 'Invited',
  PENDING: 'Pending',
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const safeName = (name) => String(name || 'event').replace(/[^\w\-]+/g, '-').replace(/-+/g, '-').slice(0, 40);

export const formatFoodPreference = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const key = raw.toUpperCase().replace(/[\s-]+/g, '_');
  if (key === 'VEG' || key === 'VEGETARIAN') return 'Veg';
  if (key === 'NON_VEG' || key === 'NONVEG' || key === 'NON_VEGETARIAN') return 'Non-veg';
  return raw;
};

export const formatDrinkPreference = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const key = raw.toUpperCase().replace(/[\s-]+/g, '_');
  if (key === 'ALCOHOL') return 'Alcohol';
  if (key === 'NON_ALCOHOL' || key === 'NONALCOHOL') return 'Non-alcohol';
  return raw;
};

const guestList = (participant) => {
  const details = participant?.responseDetails || {};
  return Array.isArray(details.guests) ? details.guests : [];
};

export const rsvpGuestRows = (participant) => {
  const details = participant?.responseDetails || {};
  const guests = guestList(participant);
  const person = participant?.userName || 'User';
  const status = STATUS_LABEL[(participant?.status || 'PENDING').toUpperCase()] || participant?.status || 'Pending';
  if (!guests.length) {
    return [{
      person,
      status,
      guest: '',
      food: formatFoodPreference(details.foodPreference),
      drinks: formatDrinkPreference(details.drinkPreference),
    }];
  }
  return guests.map((g, index) => ({
    person,
    status,
    guest: (g?.name && String(g.name).trim()) || `Guest ${index + 1}`,
    food: formatFoodPreference(g?.foodPreference),
    drinks: formatDrinkPreference(g?.drinkPreference),
  }));
};

const customAnswerList = (participant) => {
  const details = participant?.responseDetails || {};
  return Array.isArray(details.customAnswers) ? details.customAnswers : [];
};

export const rsvpColumnConfig = (rsvpFields) => {
  const fields = rsvpFields && typeof rsvpFields === 'object' ? rsvpFields : {};
  const on = (id) => {
    const cfg = fields[id];
    if (!cfg || typeof cfg !== 'object') return false;
    return cfg.enabled === true || cfg.enabled === 'true' || cfg.enabled === 1;
  };
  return {
    guestCount: on('guestCount'),
    guestNames: on('guestNames'),
    guestAges: on('guestAges'),
    food: on('foodPreference'),
    drinks: on('drinkPreference'),
  };
};

export const collectCustomFieldColumns = (participants = [], rsvpFields) => {
  const columns = [];
  const seen = new Set();
  const add = (id, label) => {
    const key = String(id || label || '').trim();
    const name = String(label || '').trim();
    if (!key || !name || seen.has(key)) return;
    seen.add(key);
    columns.push({ id: key, label: name });
  };
  const configured = Array.isArray(rsvpFields?.customFields) ? rsvpFields.customFields : [];
  configured.forEach((field) => add(field?.id, field?.label));
  participants.forEach((participant) => {
    customAnswerList(participant).forEach((item) => add(item?.id || item?.label, item?.label));
  });
  return columns;
};

const exportColumnConfig = (participants, rsvpFields) => {
  const columns = rsvpColumnConfig(rsvpFields);
  participants.forEach((participant) => {
    const details = participant?.responseDetails || {};
    if (details.guestCount != null && details.guestCount !== '') columns.guestCount = true;
    guestList(participant).forEach((guest) => {
      if (String(guest?.name || '').trim()) columns.guestNames = true;
      if (guest?.age != null && guest.age !== '') columns.guestAges = true;
      if (String(guest?.foodPreference || '').trim()) columns.food = true;
      if (String(guest?.drinkPreference || '').trim()) columns.drinks = true;
    });
  });
  return columns;
};

export const buildFullRsvpRows = (participants = [], rsvpFields) => {
  const columns = exportColumnConfig(participants, rsvpFields);
  const customColumns = collectCustomFieldColumns(participants, rsvpFields);
  const headers = [
    'Name',
    'Status',
    'Forwarded by',
    columns.guestCount ? 'People' : null,
    columns.guestNames ? 'Guest' : null,
    columns.guestAges ? 'Age' : null,
    columns.food ? 'Food' : null,
    columns.drinks ? 'Drinks' : null,
    ...customColumns.map((col) => col.label),
  ].filter(Boolean);

  const rows = participants.flatMap((participant) => {
    const cell = rsvpCell(participant);
    const guests = guestList(participant);
    const forwardedBy = String(participant?.forwardedBy || '').trim();
    const sourceRows = guests.length ? rsvpGuestRows(participant) : [{ person: cell.name, status: cell.status, guest: '', food: '', drinks: '' }];
    return sourceRows.map((row, index) => {
      const guest = guests[index] || {};
      const first = index === 0;
      const values = [
        cell.name,
        first ? cell.status : '',
        first ? forwardedBy : '',
        columns.guestCount ? (first ? (cell.guestCount || '') : '') : null,
        columns.guestNames ? (row.guest || '') : null,
        columns.guestAges ? (guest.age != null && guest.age !== '' ? String(guest.age) : '') : null,
        columns.food ? (row.food || '') : null,
        columns.drinks ? (row.drinks || '') : null,
        ...customColumns.map((col) => (first ? customAnswerValue(participant, col) : '')),
      ].filter((value) => value !== null);
      return values;
    });
  });

  return { headers, rows, columns, customColumns };
};

const csvEscape = (value) => {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

export const customAnswerValue = (participant, column) => {
  const match = customAnswerList(participant).find((item) => {
    const id = String(item?.id || '').trim();
    const label = String(item?.label || '').trim();
    return id === column.id || label === column.label || id === column.label;
  });
  return String(match?.value || '').trim();
};

export const rsvpCell = (participant) => {
  const details = participant?.responseDetails || {};
  const guests = guestList(participant);
  const rows = rsvpGuestRows(participant);
  return {
    name: participant?.userName || 'User',
    role: participant?.role || 'REQUIRED',
    status: STATUS_LABEL[(participant?.status || 'PENDING').toUpperCase()] || participant?.status || 'Pending',
    guestCount: details.guestCount != null ? String(details.guestCount) : (guests.length ? String(guests.length) : ''),
    guestNames: guests.map((g) => g?.name).filter(Boolean).join('; '),
    ages: guests.map((g) => (g?.age != null && g.age !== '' ? String(g.age) : '')).filter(Boolean).join('; '),
    food: rows.map((r) => r.food).filter(Boolean).join('; '),
    drinks: rows.map((r) => r.drinks).filter(Boolean).join('; '),
    rows,
  };
};

export const summarizeResponses = (participants = []) => {
  const counts = { invited: 0, responded: 0, accepted: 0, maybe: 0, declined: 0, pending: 0 };
  participants.forEach((p) => {
    const status = (p.status || 'PENDING').toUpperCase();
    counts.invited += 1;
    if (status === 'ACCEPTED') {
      counts.accepted += 1;
      counts.responded += 1;
    } else if (status === 'TENTATIVE') {
      counts.maybe += 1;
      counts.responded += 1;
    } else if (status === 'DECLINED') {
      counts.declined += 1;
      counts.responded += 1;
    } else {
      counts.pending += 1;
    }
  });
  return counts;
};

export const downloadEventExcel = ({ eventName, participants, rsvpFields }) => {
  const { headers, rows } = buildFullRsvpRows(participants, rsvpFields);
  const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\r\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `${safeName(eventName)}-rsvp.csv`);
};

export const pollResponseLabel = (participant, eventInfo) => {
  const details = participant?.responseDetails || {};
  const fromField = Array.isArray(participant?.selectedPollOptions) ? participant.selectedPollOptions : [];
  const fromDetails = Array.isArray(details.selectedOptions) ? details.selectedOptions : [];
  const votes = (fromField.length ? fromField : fromDetails)
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  if (votes.length) return votes.join(', ');
  const userId = participant?.userId;
  const mapped = userId && eventInfo?.pollVotesByUserId ? eventInfo.pollVotesByUserId[userId] : null;
  if (Array.isArray(mapped) && mapped.length) {
    return mapped.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
  }
  return 'No vote';
};

export const downloadPollExcel = ({ eventName, participants, eventInfo }) => {
  const headers = ['Participant', 'Response'];
  const rows = (participants || []).map((p) => [
    p.userName || 'User',
    pollResponseLabel(p, eventInfo),
  ]);
  const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\r\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `${safeName(eventName)}-poll.csv`);
};

export const downloadPollReport = ({ eventName, participants, eventInfo }) => {
  const voted = (participants || []).filter((p) => pollResponseLabel(p, eventInfo) !== 'No vote').length;
  const lines = [
    `Poll report: ${eventName || 'Poll'}`,
    `Generated: ${new Date().toLocaleString()}`,
    '',
    `Participants: ${(participants || []).length}`,
    `Voted: ${voted}`,
    `No vote: ${(participants || []).length - voted}`,
    '',
    'Participant | Response',
    ...(participants || []).map((p) => `${p.userName || 'User'} | ${pollResponseLabel(p, eventInfo)}`),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
  downloadBlob(blob, `${safeName(eventName)}-poll-report.txt`);
};

export const downloadEventReport = ({ eventName, participants, forwards, rsvpFields }) => {
  const counts = summarizeResponses(participants);
  const { headers, rows } = buildFullRsvpRows(participants, rsvpFields);
  const lines = [
    `Event report: ${eventName || 'Event'}`,
    `Generated: ${new Date().toLocaleString()}`,
    '',
    'Summary',
    `Invited: ${counts.invited}`,
    `Responded: ${counts.responded}`,
    `Accepted: ${counts.accepted}`,
    `Maybe: ${counts.maybe}`,
    `Declined: ${counts.declined}`,
    `Pending: ${counts.pending}`,
    '',
    'RSVP responses',
    headers.join(' | '),
    ...rows.map((row) => row.join(' | ')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
  downloadBlob(blob, `${safeName(eventName)}-report.txt`);
};
